import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  Flex,
  FlexProps,
  Icon,
  Img,
  Link as ALink,
  Text,
} from "@chakra-ui/react";
import { CiMail } from "react-icons/ci";
import { FaPhoneAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { BLACK_BG_COLOR, BLUE_COLOR, IMAGE_PATH, MAX_CONTENT_WIDTH, SITE_MIN_MARGIN, YELLOW_COLOR } from "../const";
import { useIsMobile } from "../hooks/useIsMobile";
import { useAreasModalStore } from "../stores/AreasModalStore";

const EMAIL_ADDRESS = "holidayhomes@breakfreeholidays.co.uk";
const PHONE_NUMBER = "0344 848 4537";

const LINKS_PROPS = {
  color: YELLOW_COLOR,
  fontSize: "13px",
};
const HEADER_PROPS = {
  color: "white",
  fontSize: "18px",
};

export const Footer: React.FC<FlexProps> = (props) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <>
        <Flex flexDir="column" bg={BLACK_BG_COLOR} p={4} py={10} mt={4} {...props}>
          <HeaderLogo imageHeight="30px" pl={4} />
          <Accordion color="white" allowMultiple>
            <AccordionItem border={0}>
              <AccordionButton>
                <Box as="span" flex="1" textAlign="left" {...HEADER_PROPS}>
                  Pages
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <PagesLinks />
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem border={0}>
              <AccordionButton>
                <Box as="span" flex="1" textAlign="left" {...HEADER_PROPS}>
                  Contact Info
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <ContactDetails />
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem border={0}>
              <AccordionButton>
                <Box as="span" flex="1" textAlign="left" {...HEADER_PROPS}>
                  Information
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <SiteInformationLinks />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Flex>
        <CopyrightBar />
      </>
    );
  }

  // Desktop
  return (
    <>
      <Flex bg={BLACK_BG_COLOR} fontSize="14px" color="white" p={SITE_MIN_MARGIN} mt={6} py={10} {...props}>
        <Flex justify="space-between" gap={4} maxW={MAX_CONTENT_WIDTH} w="100%" mx="auto">
          <HeaderLogo />
          <PagesLinks />
          <ContactDetails />
          <SiteInformationLinks />
        </Flex>
      </Flex>

      <CopyrightBar />
    </>
  );
};

const ContactDetails: React.FC = () => (
  <Flex width="max-content" flexDir="column" {...LINKS_PROPS}>
    <ALink href={`tel:${PHONE_NUMBER}`} style={{ color: "inherit", textDecoration: "none" }}>
      <Flex alignItems="center" gap={2}>
        <Icon as={FaPhoneAlt} boxSize="15px" color="yellow" />
        <Text>{PHONE_NUMBER}</Text>
      </Flex>
    </ALink>
    <ALink href={`mailto:${EMAIL_ADDRESS}`} target="_blank" style={{ color: "inherit", textDecoration: "none" }}>
      <Flex alignItems="center" gap={2}>
        <Icon as={CiMail} boxSize="15px" color="yellow" mt="3px" />
        <Text>{EMAIL_ADDRESS}</Text>
      </Flex>
    </ALink>
    <Divider borderColor={YELLOW_COLOR} borderTopWidth="1.5px" borderBottomWidth="1.5px" my="8px" w="280px" />
    <Text>Breakfree Holidays Ltd</Text>
    <Text>42 Phoenix Court Hawkins Road</Text>
    <Text>Colchester</Text>
    <Text>CO2 8JY</Text>
  </Flex>
);

const PagesLinks: React.FC = () => {
  const { onOpen } = useAreasModalStore(["onOpen"]);

  const pagesLinks = [
    { text: "Home", url: "" },
    { text: "Buying Information", url: "blog" },
    { text: "Caravan Parks", onClick: onOpen },
    { text: "Insurance", url: "insurance" },
  ];

  return (
    <Flex width="max-content" flexDir="column" {...LINKS_PROPS}>
      {pagesLinks.map(({ text, url, onClick }, i) =>
        onClick ? (
          <Box as="button" key={i} onClick={onClick} style={{ all: "unset", cursor: "pointer" }}>
            {text}
          </Box>
        ) : (
          <Link key={i} to={url!}>
            {text}
          </Link>
        ),
      )}
    </Flex>
  );
};

const SiteInformationLinks: React.FC = () => {
  const siteInformationLinks = [
    { text: "Terms & Conditions", url: "terms-and-conditions" },
    { text: "Privacy Policy", url: "privacy-policy" },
    { text: "Cookies Disclaimer", url: "cookie-policy" },
  ];

  return (
    <Flex width="max-content" flexDir="column" {...LINKS_PROPS}>
      {siteInformationLinks.map(({ text, url }, i) => (
        <Link key={i} to={url}>
          {text}
        </Link>
      ))}
    </Flex>
  );
};

const CopyrightBar: React.FC<FlexProps> = (props) => (
  <Box
    bg={BLUE_COLOR}
    minH="30px"
    h="max-content"
    w="100%"
    fontSize="14px"
    px={SITE_MIN_MARGIN}
    py={1}
    color="white"
    align="center"
    {...props}
  >
    <Flex maxW={MAX_CONTENT_WIDTH} mx="auto">
      Breakfree Holidays is registered in England No. 05753643 | Copyright © Breakfree Holidays. All rights reserved
    </Flex>
  </Box>
);

const HeaderLogo: React.FC<{ imageHeight?: string } & FlexProps> = ({ imageHeight = "40px", ...rest }) => (
  <Flex {...rest}>
    <Link to="">
      <Img src={`${IMAGE_PATH}/breakfree-holiday-homes-dark.svg`} h={imageHeight} />
    </Link>
  </Flex>
);
